import { Badge } from 'antd';
import React, { useEffect, useState, useRef, useContext, useMemo } from 'react'
import { matchPath, useLocation } from 'react-router';
import MultiSelectEntity from '../RefineSearch/TgMultiSelectEntity'
import FilterWrapper from './FilterWrapper';
import { filterAcquirers, filterReAcquirers } from "../../Config/config"
import useScrollIntoView from './useScrollIntoView';
import { PeFilterContext } from '../../Contexts/PeFilterContext';
import routePathSegments from '../../routeSegments';

function Acquirers({ open, accordionClick, options, setOptions, customPathname, label }) {
    const [companyVal, setCompanyVal] = useState("");
    const [fetchedData, setFetchedData] = useState([]);
    const [isFocused, setIsFocused] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);
    const shouldScroll = open || fetchedData.length > 0;
    const listItemRef = useScrollIntoView(shouldScroll);
    const { accessControl, refineSearchFilters } = useContext(PeFilterContext);
    const location = useLocation();
    const reMatch = matchPath(`${routePathSegments.re}/*`, location.pathname);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (companyVal.trim() !== "") {
                fetchData();
            } else {
                setShowDropdown(false);
            }
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [companyVal]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
                inputRef.current && !inputRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const payload = {
        search_key: companyVal,
    }

    if (customPathname.includes("/re/")) {
        payload.search_key = companyVal;
    } else {
        payload.industry_ids = accessControl?.maindustries?.id?.length > 0 ? accessControl?.maindustries?.id : [];
        payload.country_ids = accessControl?.ma_countries?.id?.length ? accessControl?.ma_countries?.id : [];
        payload.region_ids = accessControl?.ma_regions?.id?.length ? accessControl?.ma_regions?.id : [];
        payload.state_ids = accessControl?.ma_stage?.id?.length ? accessControl?.ma_states?.id : [];
        payload.city_ids = accessControl?.ma_cities?.id?.length ? accessControl?.ma_cities?.id : [];
    }

    const fetchData = () => {
        fetch(reMatch ? filterReAcquirers : filterAcquirers, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response.json();
            })
            .then(data => {
                if (data?.results) {
                    setFetchedData(data?.results);
                    setShowDropdown(true);
                }
            })
            .catch(error => {
                console.error('Error fetching data:', error);
                setFetchedData([]);
                setShowDropdown(false);
            });
    }

    const acquirers = useMemo(() =>
        refineSearchFilters?.[customPathname]?.acquirers?.filter(op => op.isChecked) || []
        , [refineSearchFilters, customPathname]);

    useEffect(() => {
        if (!fetchedData || !Array.isArray(fetchedData)) return;

        setOptions(prev => {
            const filterItems = refineSearchFilters?.[customPathname]?.acquirers || [];

            const itemMap = new Map();

            fetchedData.forEach(item => {
                itemMap.set(item.id, { ...item, isChecked: false });
            });

            (prev || []).forEach(item => {
                if (item.isChecked) {
                    itemMap.set(item.id, item);
                }
            });

            filterItems.forEach(item => {
                itemMap.set(item.id, { ...item, isChecked: true });
            });

            return Array.from(itemMap.values());
        });
    }, [fetchedData, acquirers, customPathname]);

    const handleIndustryChange = ((e) => {
        const { name, checked, id } = e.target;
        let currentIndustry = [];

        if (name === "allSelect") {
            currentIndustry = options.map((industry) =>
                ({ ...industry, isChecked: checked })
            );
            setOptions(currentIndustry);
        } else {
            currentIndustry = options.map((industry) =>
                industry.id === parseInt(id) ? { ...industry, isChecked: checked } : industry
            );
            setOptions(currentIndustry);
        }
    });

    const selectedIndustryLength = options?.filter(option => option.isChecked)?.length ?? 0;
    const hasValue = companyVal || selectedIndustryLength > 0;

    return (
        <div className="relative">
            {/* Search Results Dropdown - Positioned BELOW the input */}
            {showDropdown && (fetchedData.length > 0 || options.some(option => option.isChecked)) && (
                <div ref={dropdownRef} className="absolute z-50 w-full top-full mt-1 bg-white border border-gray-100 rounded-lg shadow-sm max-h-64 overflow-y-auto">
                    <MultiSelectEntity
                        fetchData={fetchData}
                        handleEntityChange={handleIndustryChange}
                        primaryEntity={options}
                        setCompanyVal={setCompanyVal}
                        customPathname={customPathname}
                        city="city"
                    />
                </div>
            )}

            <div className={`absolute left-3 transition-all duration-200 pointer-events-none flex items-center gap-2 z-10 ${isFocused || hasValue
                    ? 'top-0 -translate-y-1/2 text-xs bg-white px-1 text-[#BC8B20]'
                    : 'top-1/2 -translate-y-1/2 text-sm text-gray-500'
                }`}>
                <span>{label}</span>
                {(isFocused || hasValue) && selectedIndustryLength > 0 && (
                    <span className="bg-white rounded-full px-2 py-0.5 text-xs font-medium border border-gray-400">
                        {selectedIndustryLength}
                    </span>
                )}
            </div>

            <input
                ref={inputRef}
                type="text"
                value={companyVal}
                onChange={(e) => setCompanyVal(e.target.value)}
                onFocus={() => {
                    setIsFocused(true);
                    const hasSelectedOptions = options.some(option => option.isChecked);
                    if (fetchedData.length > 0 || hasSelectedOptions) {
                        setShowDropdown(true);
                    }
                }}
                onBlur={() => {
                    setTimeout(() => setIsFocused(false), 200);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                placeholder=""
            />
        </div>
    )
}

export default Acquirers;
